import React from 'react';
import cx from 'classnames';
import useThemeBlock from '@/hooks/useThemeBlock';
import { Section } from '@/helpers/Section';
import { LinkProps } from '@/helpers/Link/render';
// styles
import { getStyles as getDfltStyles } from '@/components/organisms/SectionOneColumn/styles.css';
import { ButtonSecondary } from '@/components/atoms/renders';
import block from './block.json';

/**
 * TYPINGS
 */
type SectionPartnersProps = SectionOneColumnProps & {
	link?: LinkProps;
};

interface ForwardedSectionPartners
	extends React.ForwardRefExoticComponent<
			SectionPartnersProps & React.RefAttributes<HTMLDivElement>
		>,
		BlockConfigs {}

/**
 * COMPONENT
 */
export const SectionPartners = React.forwardRef<
	HTMLDivElement,
	SectionPartnersProps
>(
	(
		{
			anchor,
			className,
			uptitle,
			title,
			introduction,
			link,
			slug,
			variant: currentVariant = 'default',
			titleTag = 'h2',
			children,
			theme = {},
			getStyles = getDfltStyles,
		},
		ref
	) => {
		const { variant, blockTheme } = useThemeBlock(
			slug,
			theme,
			currentVariant
		);

		const trimmedSlug = slug.split('/');
		const modifierSlug = trimmedSlug[trimmedSlug.length - 1];

		const rootClass = cx(
			'supt-section supt-section--oneCol',
			`supt-section--${modifierSlug}`,
			getStyles(blockTheme),
			className
		);

		return (
			<Section
				id={anchor}
				className={rootClass}
				variant={variant}
				slug={slug}
				ref={ref}
			>
				<div className="supt-section__inner">
					{(uptitle || title || introduction) && (
						<div className="supt-section__headline">
							{uptitle && <Section.Uptitle text={uptitle} />}
							{title && (
								<Section.Title
									text={title}
									titleTag={titleTag}
								/>
							)}
							{introduction && (
								<Section.Introduction text={introduction} />
							)}
						</div>
					)}
					{link?.href ? (
						<div className="supt-section__linkWrapper">
							<ButtonSecondary {...link} />
						</div>
					) : null}
					<div className="supt-section__content">{children}</div>
				</div>
			</Section>
		);
	}
) as ForwardedSectionPartners;

SectionPartners.slug = block.slug;
SectionPartners.title = block.title;
